import { PublicationContext } from "common/context/Publication";
import { publicationTypes } from "common/context/Publication/PublicationContext";
import { BreadcrumbJsonLd, NewsArticleJsonLd, NextSeo } from "next-seo";
import Head from "next/head";
import { useContext } from "react";
import { ArticleContext } from "../Context";
import { articleTypes } from "../Context/ArticleContext";
import CMP from "Components/CMP";

const Meta = () => {
  const { refactoredArticle } = useContext<articleTypes>(ArticleContext);
  const { publication, config } =
    useContext<publicationTypes>(PublicationContext);
  const {
    url,
    meta_title,
    title,
    images,
    section,
    body,
    keywords,
    description,
    datePublished,
    dateModified,
    authorName,
    publisherName,
    dateCreated,
    breadCrumbs,
    site_name,
    openGraphImages,
    sectionName,
    tags,
    chartBeatName,
    chartBeatAuthor,
    titleSocial,
    structuredDataMetaTitle,
    premium,
  } = refactoredArticle;

  const logos: { [unit: string]: string } = {
    ["petersfieldpost.co.uk"]: "/images/logos/peters.png",
    ["farnhamherald.com"]: "/images/logos/FarnhamHerald.jpg",
    ["altonherald.com"]: "/images/logos/AltonHerald.png",
    ["bordonherald.com"]: "/images/logos/bordan_herald.png",
    ["haslemereherald.com"]: "/images/logos/haslemere_herald.png",
    ["liphookherald.com"]: "/images/logos/lipton.png",
    ["abergavennychronicle.com"]: "/images/logos/abergavennychronicle_2.png",
    ["monmouthshirebeacon.co.uk"]: "/images/logos/monmouthshirebeacon.jpg",
    ["chepstowbeacon.co.uk"]: "/images/logos/chepstowbeacon.jpg",
    ["brecon-radnor.co.uk"]: "/images/logos/brecon_radnor.jpg",
    ["cambrian-news.co.uk"]: "/images/logos/cambrian_news.jpg",
    ["tenby-today.co.uk"]: "/images/logos/tenby_today.jpg",
    ["narberth-and-whitland-today.co.uk"]:
      "/images/logos/narberth_and_whitland_today.jpg",
    ["pembroke-today.co.uk"]: "/images/logos/pembroke.jpg",
    ["theforester.co.uk"]: "/images/logos/theforester.jpg",
    ["rossgazette.com"]: "/images/logos/rossgazette.jpg",
    ["theforestreview.co.uk"]: "/images/logos/theforestreview_2.jpg",
    ["tavistock-today.co.uk"]: "/images/logos/TavistockTimes1.jpg",
    ["middevonadvertiser.co.uk"]: "/images/logos/MidDevonAdvertiser.jpg",
    ["dawlish-today.co.uk"]: "/images/logos/DawlishGazette.jpg",
    ["teignmouth-today.co.uk"]: "/images/logos/TeignmouthPost.jpg",
    ["dartmouth-today.co.uk"]: "/images/logos/DartmouthChronicle.jpg",
    ["ivybridge-today.co.uk"]: "/images/logos/Ivybridge&SouthBrentGazette.jpg",
    ["kingsbridge-today.co.uk"]: "/images/logos/kingsbridge-today.jpg",
    ["southhams-today.co.uk"]: "/images/logos/kingsbridge-today.jpg",
    ["totnes-today.co.uk"]: "/images/logos/TotnesTimes.jpg",
    ["tamarvalleytimes.co.uk"]: "/images/logos/tamarvalleytimes.jpg",
    ["okehampton-today.co.uk"]: "/images/logos/okehampton-today.jpg",
    ["creditoncourier.co.uk"]: "/images/logos/creditoncourier.jpg",
    ["iomtoday.co.im"]: "/images/logos/iom_logo.jpg",
    ["mnrjournal.co.uk"]: "/images/logos/mnr.jpg",
    ["wsfp.co.uk"]: "/images/logos/west_sumerset.jpg",
    ["wellington-today.co.uk"]: "/images/logos/wellington.jpg",
    ["holsworthy-today.co.uk"]: "/images/logos/holsworth.jpg",
    ["cornish-times.co.uk"]: "/images/logos/cornish_times.jpg",
    ["thepost.uk.com"]: "/images/logos/cornish.jpg",
    ["bude-today.co.uk"]: "/images/logos/Bude.jpg",
    ["wokingnewsandmail.co.uk"]: "/images/logos/Bude.jpg",
  };
  const pub = publication || "";

  return (
    <>
      <CMP />
      <NextSeo
        title={meta_title}
        description={description}
        openGraph={{
          title: titleSocial,
          description: description,
          url: url,
          site_name: site_name,
          type: "article",
          images: openGraphImages,
          article: {
            publishedTime: datePublished,
            modifiedTime: dateModified,
            section: sectionName,
            tags: tags,
          },
        }}
      />
      <NewsArticleJsonLd
        url={url}
        title={structuredDataMetaTitle.replace(/"/g, "")}
        images={images}
        section={section}
        keywords={keywords}
        datePublished={datePublished}
        dateModified={dateModified}
        authorName={authorName}
        dateCreated={dateCreated}
        publisherName={publisherName}
        description={description}
        publisherLogo={`https://www.${publication}${logos[pub]}`}
        body={description.replaceAll('"', "")}
      />
      <BreadcrumbJsonLd itemListElements={breadCrumbs} />

      <Head>
        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                /** CONFIGURATION START **/
                var _sf_async_config = window._sf_async_config = (window._sf_async_config || {});
                _sf_async_config.uid = 65613;
                _sf_async_config.domain = ${JSON.stringify(publication)}
                _sf_async_config.flickerControl = false;
                _sf_async_config.useCanonical = true;
                _sf_async_config.useCanonicalDomain = true;
                _sf_async_config.sections = ${JSON.stringify(chartBeatName)};
                _sf_async_config.authors = ${JSON.stringify(chartBeatAuthor)};
                _sf_async_config.title = ${JSON.stringify(title)};
                _sf_async_config.pagetype = 'article';
                _sf_async_config.path = ${JSON.stringify(url)};
                /** CONFIGURATION END **/
                function loadChartbeat() {
                var e = document.createElement('script');
                var n = document.getElementsByTagName('script')[0];
                e.type = 'text/javascript';
                e.async = true;
                e.src = '//static.chartbeat.com/js/chartbeat.js';
                n.parentNode.insertBefore(e, n);
                }
                loadChartbeat();
                })();
              `,
          }}
        />
        <script async src="//static.chartbeat.com/js/chartbeat_mab.js"></script>

        {/* Google Tag Manager */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${config.gtm_id}');
        `,
          }}
        />
        {/* End Google Tag Manager */}

        <script
          dangerouslySetInnerHTML={{
            __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', '${config.region}');
`,
          }}
        />

        <script
          async
          src={`https://www.googletagmanager.com/gtag/js?id=${config.region}`}
        />
      </Head>
    </>
  );
};
export default Meta;
